import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Blog } from "@/types/blog";
import { fetchBlogBySlug, trackBlogUpvote } from "@/services/blog/blogService";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { Heart, Calendar, User, Clock, Share2, BookOpen } from "lucide-react";
import { Helmet } from "react-helmet";
import { cn } from "@/lib/currencyFormatter";
import { useTranslation } from "react-i18next";

export function BlogDetail() {
  const { slug } = useParams<{ slug: string }>();
  const [blog, setBlog] = useState<Blog | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [upvoted, setUpvoted] = useState(false);
  const [upvoteCount, setUpvoteCount] = useState(0);
  const { toast } = useToast();
  const navigate = useNavigate();
  const [readingTime, setReadingTime] = useState("5 min");
  const { t } = useTranslation();

  useEffect(() => {
    const loadBlog = async () => {
      if (!slug) return;

      try {
        setLoading(true);
        const fetchedBlog = await fetchBlogBySlug(slug);
        setBlog(fetchedBlog);
        setUpvoteCount(fetchedBlog.upvotes);

        // Calculate reading time
        const wordCount = fetchedBlog.content
          .filter((section) => section.type === "paragraph")
          .reduce(
            (count, section) => count + section.content.split(/\s+/).length,
            0
          );
        const time = Math.ceil(wordCount / 200); // Assuming 200 words per minute
        setReadingTime(`${time} min read`);
      } catch (err) {
        console.error("Failed to load blog:", err);
        setError("This blog could not be found or is no longer available.");
      } finally {
        setLoading(false);
      }
    };

    loadBlog();
  }, [slug]);

  const handleUpvote = async () => {
    if (!blog || upvoted) return;

    try {
      const success = await trackBlogUpvote(blog.id);
      if (success) {
        setUpvoted(true);
        setUpvoteCount((prev) => prev + 1);
        toast({
          title: t("blogDetail.upvoteSuccessTitle"),
          description: t("blogDetail.upvoteSuccessDescription"),
        });
      } else {
        toast({
          variant: "destructive",
          title: t("blogDetail.alreadyUpvotedTitle"),
          description: t("blogDetail.alreadyUpvotedDescription"),
        });
      }
    } catch (err) {
      console.error("Failed to upvote:", err);
      toast({
        variant: "destructive",
        title: t("blogDetail.upvoteFailedTitle"),
        description: t("blogDetail.upvoteFailedDescription"),
      });
    }
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator
        .share({
          title: blog?.title || "Xerven Blog",
          text: blog?.summary || "",
          url: window.location.href,
        })
        .then(() => {
          toast({
            title: "Shared successfully!",
            description: "Thanks for sharing our content.",
          });
        })
        .catch((error) => {
          console.error("Error sharing:", error);
        });
    } else {
      navigator.clipboard.writeText(window.location.href);
      toast({
        title: "Link copied to clipboard",
        description: "You can now paste the link anywhere.",
      });
    }
  };

  if (loading) {
    return (
      <div className="bg-gradient-to-b from-white to-slate-50 dark:from-slate-950 dark:to-slate-900 min-h-screen pt-10">
        <div className="container max-w-4xl mx-auto py-10 px-4 animate-pulse">
          <div className="h-10 w-3/4 bg-gray-200 dark:bg-gray-700 rounded-lg mb-4"></div>
          <div className="h-6 w-1/2 bg-gray-200 dark:bg-gray-700 rounded-lg mb-6"></div>
          <div className="aspect-video w-full bg-gray-200 dark:bg-gray-700 rounded-lg mb-8"></div>
          <div className="space-y-4">
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded-lg w-full"></div>
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded-lg w-full"></div>
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded-lg w-5/6"></div>
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded-lg w-full"></div>
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded-lg w-3/4"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !blog) {
    return (
      <div className="bg-gradient-to-b from-white to-slate-50 dark:from-slate-950 dark:to-slate-900 min-h-screen pt-10">
        <div className="container max-w-4xl mx-auto py-20 px-4 text-center">
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl p-12">
            <h1 className="text-3xl font-bold mb-6 text-gray-800 dark:text-white">
              {t("blogDetail.blogNotFoundTitle")}
            </h1>
            <p className="text-muted-foreground text-lg mb-8">
              {error || "The requested blog could not be found."}
            </p>
            <Button variant="outline" onClick={() => navigate("/blog")}>
              {t("blogDetail.backToAllBlogs")}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const formattedDate = new Date(blog.createdAt).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  return (
    <>
      <Helmet>
        <title>{blog.title} | Xerven Blog</title>
        <meta
          name="description"
          content={blog.metaDescription || blog.summary}
        />
        {blog.metaKeywords && (
          <meta name="keywords" content={blog.metaKeywords} />
        )}
        <meta property="og:title" content={blog.title} />
        <meta
          property="og:description"
          content={blog.metaDescription || blog.summary}
        />
        {blog.coverImage && (
          <meta property="og:image" content={blog.coverImage} />
        )}
        <meta property="og:type" content="article" />
        <meta property="article:published_time" content={blog.createdAt} />
        <meta property="article:author" content={blog.authorName || "Xerven"} />
      </Helmet>

      <div className="bg-gradient-to-b from-white to-slate-50 dark:from-slate-950 dark:to-slate-900 min-h-screen pt-10">
        <div className="container max-w-4xl mx-auto py-10 px-4">
          {/* Hero Section with Cover Image */}
          {blog.coverImage && (
            <div className="relative mb-10 rounded-2xl overflow-hidden shadow-2xl">
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent z-10"></div>
              <img
                src={blog.coverImage}
                alt={blog.title}
                className="w-full h-96 object-cover"
              />
              <div className="absolute bottom-0 left-0 right-0 p-8 z-20">
                <div className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-primary/90 text-white mb-4">
                  <BookOpen className="h-3 w-3 mr-1" />
                  Xerven Blog
                </div>
                <h1 className="text-4xl md:text-5xl font-bold text-white mb-2 leading-tight">
                  {blog.title}
                </h1>
                <div className="flex flex-wrap items-center text-white/90 text-sm space-x-4 mt-4">
                  {blog.authorName && (
                    <div className="flex items-center">
                      <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center mr-2">
                        <User className="h-4 w-4" />
                      </div>
                      <span>{blog.authorName}</span>
                    </div>
                  )}
                  <div className="flex items-center">
                    <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center mr-2">
                      <Calendar className="h-4 w-4" />
                    </div>
                    <span>{formattedDate}</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center mr-2">
                      <Clock className="h-4 w-4" />
                    </div>
                    <span>{readingTime}</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Main Content */}
          <div className="relative">
            {/* Social sharing sidebar */}
            <div className="hidden md:flex fixed left-8 top-1/3 flex-col items-center space-y-4">
              <Button
                variant="outline"
                size="icon"
                className={cn(
                  "rounded-full bg-white dark:bg-slate-800 shadow-md hover:shadow-lg transition-all",
                  upvoted && "bg-red-50 text-red-500 border-red-200"
                )}
                onClick={handleUpvote}
              >
                <Heart className={cn("h-5 w-5", upvoted && "fill-red-500")} />
              </Button>
              <span className="text-sm font-medium">{upvoteCount}</span>

              <Button
                variant="outline"
                size="icon"
                className="rounded-full bg-white dark:bg-slate-800 shadow-md hover:shadow-lg transition-all mt-6"
                onClick={handleShare}
              >
                <Share2 className="h-5 w-5" />
              </Button>
            </div>

            <article className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl p-8 md:p-12">
              {!blog.coverImage && (
                <h1 className="text-4xl md:text-5xl font-bold mb-6 text-gray-800 dark:text-white leading-tight">
                  {blog.title}
                </h1>
              )}

              {!blog.coverImage && (
                <div className="flex flex-wrap items-center text-muted-foreground text-sm space-x-4 mb-8">
                  {blog.authorName && (
                    <div className="flex items-center">
                      <User className="h-4 w-4 mr-2" />
                      <span>{blog.authorName}</span>
                    </div>
                  )}
                  <div className="flex items-center">
                    <Calendar className="h-4 w-4 mr-2" />
                    <span>{formattedDate}</span>
                  </div>
                  <div className="flex items-center">
                    <Clock className="h-4 w-4 mr-2" />
                    <span>{readingTime}</span>
                  </div>
                </div>
              )}

              <div className="text-xl font-medium text-gray-600 dark:text-gray-300 mb-10 leading-relaxed border-l-4 border-primary pl-6 py-2 italic bg-primary/5 rounded-sm">
                {blog.summary}
              </div>

              <div className="prose prose-lg dark:prose-invert max-w-none">
                {blog.content.map((section, index) => {
                  switch (section.type) {
                    case "paragraph":
                      return (
                        <div
                          key={index}
                          className="mb-8 animate-fade-in"
                          style={{ animationDelay: `${index * 0.1}s` }}
                        >
                          <p className="text-lg leading-relaxed text-gray-700 dark:text-gray-200">
                            {section.content}
                          </p>
                        </div>
                      );
                    case "image":
                      return (
                        <div
                          key={index}
                          className="my-12 animate-fade-in"
                          style={{ animationDelay: `${index * 0.1}s` }}
                        >
                          <div className="rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300">
                            <img
                              src={section.content}
                              alt={section.caption || `Image ${index + 1}`}
                              className="w-full h-auto"
                            />
                          </div>
                          {section.caption && (
                            <p className="text-sm text-center text-muted-foreground mt-3 italic">
                              {section.caption}
                            </p>
                          )}
                        </div>
                      );
                    case "video":
                      return (
                        <div
                          key={index}
                          className="my-12 animate-fade-in"
                          style={{ animationDelay: `${index * 0.1}s` }}
                        >
                          <div className="aspect-video rounded-xl overflow-hidden shadow-lg">
                            <iframe
                              src={section.content}
                              title={section.caption || `Video ${index + 1}`}
                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                              allowFullScreen
                              className="w-full h-full"
                            ></iframe>
                          </div>
                          {section.caption && (
                            <p className="text-sm text-center text-muted-foreground mt-3 italic">
                              {section.caption}
                            </p>
                          )}
                        </div>
                      );
                    default:
                      return null;
                  }
                })}
              </div>
            </article>

            {/* Mobile social bar */}
            <div className="md:hidden flex justify-center mt-6 space-x-6">
              <Button
                variant="outline"
                size="sm"
                className={cn(
                  "rounded-full bg-white dark:bg-slate-800 shadow-md flex items-center space-x-2",
                  upvoted && "bg-red-50 text-red-500 border-red-200"
                )}
                onClick={handleUpvote}
              >
                <Heart className={cn("h-4 w-4", upvoted && "fill-red-500")} />
                <span>{upvoteCount}</span>
              </Button>

              <Button
                variant="outline"
                size="sm"
                className="rounded-full bg-white dark:bg-slate-800 shadow-md flex items-center space-x-2"
                onClick={handleShare}
              >
                <Share2 className="h-4 w-4" />
                <span>Share</span>
              </Button>
            </div>

            {/* Author card */}
            <div className="mt-12">
              <Card className="overflow-hidden border-none shadow-xl hover:shadow-2xl transition-all duration-300 bg-gradient-to-br from-slate-50 to-white dark:from-slate-900 dark:to-slate-800">
                <CardContent className="p-8">
                  <div className="flex flex-col md:flex-row items-center md:items-start text-center md:text-left gap-6">
                    <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <User className="h-8 w-8 text-primary" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold mb-2">
                        {blog.authorName || "Xerven Team"}
                      </h3>
                      <p className="text-muted-foreground mb-4">
                        Writer at Xerven Blog, focusing on payment technologies
                        and financial innovations.
                      </p>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => navigate("/blog")}
                        className="bg-primary/10 hover:bg-primary/20 text-primary"
                      >
                        {t("index.viewAllArticles")}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Related articles placeholder */}
            <div className="mt-12">
              <h2 className="text-2xl font-bold mb-6">
                {t("blogDetail.relatedArticles")}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {[1, 2].map((i) => (
                  <div
                    key={i}
                    className="bg-white dark:bg-slate-800 rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 hover:translate-y-[-5px] cursor-pointer"
                    onClick={() => navigate("/blog")}
                  >
                    <div className="h-40 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 animate-pulse"></div>
                    <div className="p-6">
                      <h3 className="font-bold mb-2">
                        {t("blogDetail.discoverMore")}
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        {t("blogDetail.exploreLatest")}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-12 pt-6 border-t flex justify-between items-center">
              <Button variant="outline" onClick={() => navigate("/blog")}>
                {t("blogDetail.backToAllBlogs")}
              </Button>
              <div className="text-sm text-muted-foreground">
                {t("blogDetail.publishedOn")} {formattedDate}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
